import type { SafeGround } from "@playarena/shared";
import { MapPin, Users, Star, Wifi, Car, Coffee } from "lucide-react";
import type React from "react";
import { Link } from "react-router-dom";

import { formatCurrency, capitalizeFirst } from "../../../utils/helpers";
import { Badge } from "../../ui/badge";
import { Button } from "../../ui/button";
import { Card, CardContent, CardHeader } from "../../ui/card";

interface GroundCardProps {
  ground: SafeGround;
  viewMode?: "grid" | "list";
}

const GroundCard: React.FC<GroundCardProps> = ({ ground, viewMode = "grid" }) => {
  const getAmenityIcon = (amenity: string) => {
    switch (amenity.toLowerCase()) {
      case "wifi":
        return <Wifi className='h-3 w-3' />;
      case "parking":
        return <Car className='h-3 w-3' />;
      case "cafe":
      case "cafeteria":
        return <Coffee className='h-3 w-3' />;
      default:
        return null;
    }
  };

  if (viewMode === "list") {
    return (
      <Card className='overflow-hidden transition-shadow hover:shadow-lg'>
        <div className='flex'>
          <div className='h-32 w-48 flex-shrink-0 bg-gradient-to-r from-green-500 to-blue-500' />
          <div className='flex-1 p-6'>
            <div className='mb-2 flex items-start justify-between'>
              <div>
                <h3 className='mb-1 text-xl font-semibold'>{ground.name}</h3>
                <div className='mb-2 flex items-center text-sm text-muted-foreground'>
                  <MapPin className='mr-1 h-4 w-4' />
                  {ground.location.city}, {ground.location.country}
                </div>
              </div>
              <div className='text-right'>
                <div className='text-lg font-bold'>
                  {formatCurrency(ground.pricing.basePrice, ground.pricing.currency)}
                </div>
                <div className='text-sm text-muted-foreground'>per hour</div>
              </div>
            </div>

            <div className='mb-3 flex items-center gap-4'>
              <Badge variant='secondary' className='capitalize'>
                {ground.type}
              </Badge>
              <div className='flex items-center text-sm text-muted-foreground'>
                <Users className='mr-1 h-4 w-4' />
                {ground.capacity.toLocaleString()} capacity
              </div>
              <div className='flex items-center text-sm text-muted-foreground'>
                <Star className='mr-1 h-4 w-4 fill-yellow-400 text-yellow-400' />
                4.5 (120 reviews)
              </div>
            </div>

            <div className='flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                {ground.amenities.slice(0, 3).map((amenity, index) => (
                  <div key={index} className='flex items-center text-xs text-muted-foreground'>
                    {getAmenityIcon(amenity)}
                    <span className='ml-1'>{capitalizeFirst(amenity)}</span>
                  </div>
                ))}
                {ground.amenities.length > 3 && (
                  <span className='text-xs text-muted-foreground'>
                    +{ground.amenities.length - 3} more
                  </span>
                )}
              </div>
              <Button asChild>
                <Link to={`/grounds/${ground.id}`}>View Details</Link>
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className='overflow-hidden transition-shadow hover:shadow-lg'>
      <div className='aspect-video bg-gradient-to-r from-green-500 to-blue-500' />
      <CardHeader className='pb-3'>
        <div className='mb-2 flex items-start justify-between'>
          <Badge variant='secondary' className='capitalize'>
            {ground.type}
          </Badge>
          <div className='flex items-center text-sm text-muted-foreground'>
            <Star className='mr-1 h-4 w-4 fill-yellow-400 text-yellow-400' />
            4.5
          </div>
        </div>
        <h3 className='line-clamp-1 text-lg font-semibold'>{ground.name}</h3>
        <div className='flex items-center text-sm text-muted-foreground'>
          <MapPin className='mr-1 h-4 w-4' />
          {ground.location.city}, {ground.location.country}
        </div>
      </CardHeader>
      <CardContent>
        <div className='mb-3 flex items-center justify-between'>
          <div className='flex items-center text-sm text-muted-foreground'>
            <Users className='mr-1 h-4 w-4' />
            {ground.capacity.toLocaleString()}
          </div>
          <div className='text-lg font-bold'>
            {formatCurrency(ground.pricing.basePrice, ground.pricing.currency)}
            <span className='text-sm font-normal text-muted-foreground'>/hour</span>
          </div>
        </div>

        <div className='mb-4 flex items-center gap-2'>
          {ground.amenities.slice(0, 3).map((amenity, index) => (
            <div key={index} className='flex items-center text-xs text-muted-foreground'>
              {getAmenityIcon(amenity)}
              <span className='ml-1'>{capitalizeFirst(amenity)}</span>
            </div>
          ))}
          {ground.amenities.length > 3 && (
            <span className='text-xs text-muted-foreground'>+{ground.amenities.length - 3}</span>
          )}
        </div>

        <Button asChild className='w-full'>
          <Link to={`/grounds/${ground.id}`}>View Details</Link>
        </Button>
      </CardContent>
    </Card>
  );
};

export default GroundCard;
